import {getHttpClient} from './httpClient.js';
import {Notification} from './notification.js';
import {triggerFlyingBookmarkAnimation} from './flyingBookmarkAnimation.js';
import {t} from '../localization/translate.js';

/**
 * Toggle a bookmark via the API. Handles the full lifecycle:
 * disable button -> API call -> animation -> event dispatch -> re-enable button.
 *
 * Each consumer is responsible for updating its own UI after this resolves.
 *
 * @param {HTMLElement} button - The button element (used for disable state, animation source, and event dispatch)
 * @param {string} bookmarkableType - The bookmarkable type slug (e.g. "video", "article")
 * @param {string} bookmarkableId - The bookmarkable model ID
 * @param {boolean} currentlyBookmarked - Current bookmark state
 * @returns {Promise<boolean|null>} The new bookmark state, or null if the toggle failed
 */
export async function toggleBookmark(button, bookmarkableType, bookmarkableId, currentlyBookmarked) {
    button.disabled = true;

    try {
        const httpClient = getHttpClient();

        if (currentlyBookmarked) {
            /** @route 'member.bookmarks.type.destroy' */
            await httpClient.delete(`/members/bookmarks/${bookmarkableType}/${bookmarkableId}`, {});

            button.dispatchEvent(new CustomEvent('bookmark:removed', {
                bubbles: true,
                detail: {bookmarkableType, bookmarkableId},
            }));

            return false;
        }

        /** @route 'member.bookmarks.store' */
        await httpClient.post('/members/bookmarks', {
            bookmarkable_type: bookmarkableType,
            bookmarkable_id: bookmarkableId,
        });

        triggerFlyingBookmarkAnimation(button);

        button.dispatchEvent(new CustomEvent('bookmark:added', {
            bubbles: true,
            detail: {bookmarkableType, bookmarkableId},
        }));

        return true;
    } catch (error) {
        Notification.error(t('Error toggling bookmark. Please try again.'));
        return null;
    } finally {
        button.disabled = false;
    }
}
